import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './InactivityTimer.css';

const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes (défaut)
const WARNING_MS = 60 * 1000;              // Avertissement 60s avant

const InactivityTimer = ({ onLogout, userLabel, timeoutMs = DEFAULT_TIMEOUT_MS }) => {
  const { t } = useTranslation('common');
  const [showWarning, setShowWarning] = useState(false);
  const [remaining, setRemaining] = useState(60);

  const logoutTimer  = useRef(null);
  const warningTimer = useRef(null);
  const countdown    = useRef(null);

  const clearAllTimers = useCallback(() => {
    clearTimeout(logoutTimer.current);
    clearTimeout(warningTimer.current);
    clearInterval(countdown.current);
  }, []);

  const resetTimer = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setRemaining(60);

    // Démarrer l'avertissement à (TIMEOUT - WARNING) ms
    warningTimer.current = setTimeout(() => {
      setShowWarning(true);
      setRemaining(60);

      // Décompte de 60 → 0
      countdown.current = setInterval(() => {
        setRemaining(prev => {
          if (prev <= 1) {
            clearInterval(countdown.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, timeoutMs - WARNING_MS);

    // Déconnexion automatique après timeoutMs
    logoutTimer.current = setTimeout(() => {
      clearAllTimers();
      onLogout();
    }, timeoutMs);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearAllTimers, onLogout]);

  // Enregistrer les événements d'activité
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(ev => window.addEventListener(ev, resetTimer, { passive: true }));
    resetTimer(); // Démarrer au montage

    return () => {
      events.forEach(ev => window.removeEventListener(ev, resetTimer));
      clearAllTimers();
    };
  }, [resetTimer, clearAllTimers]);

  if (!showWarning) return null;

  return (
    <div className="inactivity-overlay" role="dialog" aria-modal="true">
      <div className="inactivity-modal">
        <div className="inactivity-icon">⚠️</div>
        <h3 className="inactivity-title">{t('inactivity.inactiveTitle')}</h3>
        <p className="inactivity-text">
          {userLabel || t('inactivity.client')} {t('inactivity.willExpireIn')}{' '}
          <strong className="inactivity-countdown">{remaining}s</strong>
        </p>
        <p className="inactivity-subtext">
          {t('inactivity.clickContinue')}
        </p>
        <div className="inactivity-progress">
          <div
            className="inactivity-progress-bar"
            style={{ width: `${(remaining / 60) * 100}%` }}
          />
        </div>
        <button className="inactivity-btn" onClick={resetTimer}>
          {t('inactivity.continueSession')}
        </button>
      </div>
    </div>
  );
};

export default InactivityTimer;
